type OptionalPromise<T> = T | Promise<T>;

const ERROR_UNAVAILABLE =
  'Telegram WebApp is not available. Ensure the app is opened inside Telegram and the script is loaded in <head>.';

export function getTelegramWebApp(): Telegram.WebApp | null {
  if (typeof window === 'undefined') return null;

  // Type assertion to handle both real Telegram.WebApp and MockTelegramWebApp
  // MockTelegramWebApp implements the same interface, so this is safe
  return (window.Telegram?.WebApp as unknown as Telegram.WebApp | undefined) ?? null;
}

export function assertTelegramWebApp(): Telegram.WebApp {
  const tg = getTelegramWebApp();
  if (!tg) throw new Error(ERROR_UNAVAILABLE);
  return tg;
}

export function withTelegramWebApp<T>(fn: (tg: Telegram.WebApp) => T): T | null {
  const tg = getTelegramWebApp();
  return tg ? fn(tg) : null;
}

export async function withTelegramWebAppAsync<T>(
  fn: (tg: Telegram.WebApp) => OptionalPromise<T>,
): Promise<T | null> {
  const tg = getTelegramWebApp();
  if (!tg) return null;
  return fn(tg);
}

const setButtonParams = (
  button:
    | Telegram.MainButton
    | Telegram.SecondaryButton
    | undefined,
  params?: Telegram.WebAppButtonParams,
) => {
  if (!button || !params) return;

  if (params.text) button.setText(params.text);
  if (params.color || params.textColor) {
    button.setParams({
      text: params.text,
      color: params.color,
      text_color: params.textColor,
    });
  }

  if (params.show === true) button.show();
  if (params.show === false) button.hide();

  if (params.showProgress) {
    button.showProgress(Boolean(params.keepShown));
  } else if (params.showProgress === false) {
    button.hideProgress();
  }
};

export function initializeTelegramUI(options?: Telegram.WebAppInitializeOptions) {
  const tg = assertTelegramWebApp();
  tg.ready();
  if (options?.expand !== false) tg.expand();

  if (options?.showBack) tg.BackButton.show();

  if (options?.bottomButton) {
    const { onClick, ...params } = options.bottomButton;
    setButtonParams(tg.MainButton, { ...params, show: true });
    if (onClick) tg.MainButton.onClick(onClick);
  }
}

const wrapSendData = (tg: Telegram.WebApp, payload: unknown) => {
  tg.sendData(typeof payload === 'string' ? payload : JSON.stringify(payload));
};

const mainButtonControls = {
  set(params?: Telegram.WebAppMainButtonOptions) {
    return withTelegramWebApp(tg => {
      const { onClick, ...rest } = params ?? {};
      setButtonParams(tg.MainButton, rest);
      if (onClick) tg.MainButton.onClick(onClick);
    });
  },
  show(params?: Telegram.WebAppMainButtonOptions) {
    return withTelegramWebApp(tg => {
      const { onClick, ...rest } = params ?? {};
      setButtonParams(tg.MainButton, { ...rest, show: true });
      if (onClick) tg.MainButton.onClick(onClick);
    });
  },
  hide() {
    return withTelegramWebApp(tg => tg.MainButton.hide());
  },
  showProgress(keepShown?: boolean) {
    return withTelegramWebApp(tg => tg.MainButton.showProgress(Boolean(keepShown)));
  },
  hideProgress() {
    return withTelegramWebApp(tg => tg.MainButton.hideProgress());
  },
  onClick(handler: () => void) {
    const tg = assertTelegramWebApp();
    tg.MainButton.onClick(handler);
    return () => tg.MainButton.onClick(() => undefined);
  },
};

const secondaryButtonControls = {
  set(params?: Telegram.WebAppSecondaryButtonOptions) {
    return withTelegramWebApp(tg => {
      if (!tg.SecondaryButton) return;
      const { onClick, ...rest } = params ?? {};
      setButtonParams(tg.SecondaryButton, rest);
      if (onClick) tg.SecondaryButton.onClick(onClick);
    });
  },
  show(params?: Telegram.WebAppSecondaryButtonOptions) {
    return withTelegramWebApp(tg => {
      if (!tg.SecondaryButton) return;
      const { onClick, ...rest } = params ?? {};
      setButtonParams(tg.SecondaryButton, { ...rest, show: true });
      if (onClick) tg.SecondaryButton.onClick(onClick);
    });
  },
  hide() {
    return withTelegramWebApp(tg => tg.SecondaryButton?.hide());
  },
};

const backButtonControls = {
  show(handler?: () => void) {
    return withTelegramWebApp(tg => {
      tg.BackButton.show();
      if (handler) tg.BackButton.onClick(handler);
    });
  },
  hide() {
    return withTelegramWebApp(tg => tg.BackButton.hide());
  },
  onClick(handler: () => void) {
    const tg = assertTelegramWebApp();
    tg.BackButton.onClick(handler);
    return () => tg.BackButton.onClick(() => undefined);
  },
};

const events = {
  on(event: Telegram.WebAppEvent, handler: Telegram.EventHandler) {
    return withTelegramWebApp(tg => tg.onEvent(event, handler));
  },
  off(event: Telegram.WebAppEvent, handler: Telegram.EventHandler) {
    return withTelegramWebApp(tg => tg.offEvent(event, handler));
  },
};

const fullscreen = {
  async request() {
    return withTelegramWebAppAsync(async tg => {
      if (!tg.requestFullscreen) return false;
      await tg.requestFullscreen();
      return true;
    });
  },
  async exit() {
    return withTelegramWebAppAsync(async tg => {
      if (!tg.exitFullscreen) return false;
      await tg.exitFullscreen();
      return true;
    });
  },
};

const orientation = {
  async lock(mode: 'portrait' | 'landscape') {
    return withTelegramWebAppAsync(async tg => {
      if (!tg.lockOrientation) return false;
      await tg.lockOrientation(mode);
      return true;
    });
  },
  async unlock() {
    return withTelegramWebAppAsync(async tg => {
      if (!tg.unlockOrientation) return false;
      await tg.unlockOrientation();
      return true;
    });
  },
};

const share = {
  async message(options: Parameters<Telegram.WebApp['shareMessage']>[0]) {
    return withTelegramWebAppAsync(async tg => {
      await tg.shareMessage(options);
      return true;
    });
  },
  async toStory(options: NonNullable<Telegram.WebApp['shareToStory']> extends (...args: infer A) => any ? A[0] : never) {
    return withTelegramWebAppAsync(async tg => {
      if (!tg.shareToStory) return false;
      await tg.shareToStory(options);
      return true;
    });
  },
  async download(options: { url: string; file_name?: string }) {
    return withTelegramWebAppAsync(async tg => {
      if (!tg.downloadFile) return false;
      await tg.downloadFile(options);
      return true;
    });
  },
};

const shortcuts = {
  async addToHomeScreen() {
    return withTelegramWebAppAsync(async tg => {
      if (!tg.addToHomeScreen) return false;
      await tg.addToHomeScreen();
      return true;
    });
  },
  async checkStatus() {
    return withTelegramWebAppAsync(async tg => tg.checkHomeScreenStatus?.() ?? 'unsupported');
  },
};

const emojiStatus = {
  async set(options: { custom_emoji_id: string; duration?: number }) {
    return withTelegramWebAppAsync(async tg => {
      if (!tg.setEmojiStatus) return false;
      await tg.setEmojiStatus(options);
      return true;
    });
  },
  async requestAccess() {
    return withTelegramWebAppAsync(async tg => tg.requestEmojiStatusAccess?.() ?? 'unsupported');
  },
};

const location = {
  async requestAccess() {
    return withTelegramWebAppAsync(async tg => tg.LocationManager?.requestAccess?.() ?? false);
  },
  async get(options?: Parameters<NonNullable<Telegram.LocationManager>['getLocation']>[0]) {
    return withTelegramWebAppAsync(async tg => {
      if (!tg.LocationManager?.getLocation) return null;
      return tg.LocationManager.getLocation(options);
    });
  },
};

const sensors = {
  async startAccelerometer(options?: { intervalMs?: number }) {
    return withTelegramWebAppAsync(async tg => {
      if (!tg.Accelerometer?.start) return false;
      await tg.Accelerometer.start(options);
      return true;
    });
  },
  async stopAccelerometer() {
    return withTelegramWebAppAsync(async tg => {
      if (!tg.Accelerometer?.stop) return false;
      await tg.Accelerometer.stop();
      return true;
    });
  },
  async startGyroscope(options?: { intervalMs?: number }) {
    return withTelegramWebAppAsync(async tg => {
      if (!tg.Gyroscope?.start) return false;
      await tg.Gyroscope.start(options);
      return true;
    });
  },
  async stopGyroscope() {
    return withTelegramWebAppAsync(async tg => {
      if (!tg.Gyroscope?.stop) return false;
      await tg.Gyroscope.stop();
      return true;
    });
  },
  async startDeviceOrientation(options?: { intervalMs?: number }) {
    return withTelegramWebAppAsync(async tg => {
      if (!tg.DeviceOrientation?.start) return false;
      await tg.DeviceOrientation.start(options);
      return true;
    });
  },
  async stopDeviceOrientation() {
    return withTelegramWebAppAsync(async tg => {
      if (!tg.DeviceOrientation?.stop) return false;
      await tg.DeviceOrientation.stop();
      return true;
    });
  },
};

export const TELEGRAM_STORAGE_TIMEOUT_MS = 1_500;

export function isTelegramVersionAtLeast(actual: string | undefined, required: string): boolean {
  if (!actual || !/^\d+(?:\.\d+)*$/.test(actual)) return false;
  const actualParts = actual.split('.').map(Number);
  const requiredParts = required.split('.').map(Number);
  for (let index = 0; index < Math.max(actualParts.length, requiredParts.length); index += 1) {
    const actualPart = actualParts[index] ?? 0;
    const requiredPart = requiredParts[index] ?? 0;
    if (actualPart !== requiredPart) return actualPart > requiredPart;
  }
  return true;
}

// Native storage returns its own API object immediately. Only the callback
// confirms that Telegram has read or persisted a value. Some clients never
// deliver that callback, so let the storage driver fall back after a deadline.
function telegramStorageOperation<T>(
  invoke: (callback: (error: string | null, value?: T) => void) => unknown,
): Promise<T | undefined> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const timeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error('Telegram storage request timed out'));
    }, TELEGRAM_STORAGE_TIMEOUT_MS);
    const complete = (error: string | null, value?: T) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      if (error) reject(new Error(error));
      else resolve(value);
    };
    try {
      invoke(complete);
    } catch (error) {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      reject(error);
    }
  });
}

function nativeStorage(name: 'DeviceStorage' | 'SecureStorage') {
  const getStorage = () => {
    const tg = getTelegramWebApp();
    return tg && isTelegramVersionAtLeast(tg.version, '9.0') ? tg[name] : undefined;
  };
  return {
    async getItem(key: string): Promise<string | null> {
      const store = getStorage();
      if (!store) return null;
      const value = await telegramStorageOperation<string | null>(cb => store.getItem(key, cb));
      if (value == null) return null;
      if (typeof value !== 'string') throw new Error('Invalid Telegram storage value');
      return value;
    },
    async setItem(key: string, value: string): Promise<boolean> {
      const store = getStorage();
      if (!store) return false;
      return (await telegramStorageOperation<boolean>(cb => store.setItem(key, value, cb))) === true;
    },
    async removeItem(key: string): Promise<boolean> {
      const store = getStorage();
      if (!store) return false;
      return (await telegramStorageOperation<boolean>(cb => store.removeItem(key, cb))) === true;
    },
    async clear(): Promise<boolean> {
      const store = getStorage();
      if (!store) return false;
      return (await telegramStorageOperation<boolean>(cb => store.clear(cb))) === true;
    },
  };
}

const storage = {
  cloud: {
    async getItem(key: string) {
      return withTelegramWebAppAsync<string | null>(tg => {
        if (!tg.CloudStorage) return null;
        return new Promise((resolve, reject) => {
          tg.CloudStorage!.getItem(key, (err, value) => {
            if (err) reject(new Error(err));
            else resolve(value ?? null);
          });
        });
      });
    },
    async setItem(key: string, value: string) {
      return withTelegramWebAppAsync<boolean>(tg => {
        if (!tg.CloudStorage) return false;
        return new Promise((resolve, reject) => {
          tg.CloudStorage!.setItem(key, value, (err, ok) => {
            if (err) reject(new Error(err));
            else resolve(Boolean(ok));
          });
        });
      });
    },
    async removeItem(key: string) {
      return withTelegramWebAppAsync<boolean>(tg => {
        if (!tg.CloudStorage) return false;
        return new Promise((resolve, reject) => {
          tg.CloudStorage!.removeItem(key, (err, ok) => {
            if (err) reject(new Error(err));
            else resolve(Boolean(ok));
          });
        });
      });
    },
    async getKeys() {
      return withTelegramWebAppAsync<string[] | null>(tg => {
        if (!tg.CloudStorage) return null;
        return new Promise((resolve, reject) => {
          tg.CloudStorage!.getKeys((err, keys) => {
            if (err) reject(new Error(err));
            else resolve(keys ?? []);
          });
        });
      });
    },
  },
  device: nativeStorage('DeviceStorage'),
  secure: nativeStorage('SecureStorage'),
};

const lifecycle = {
  ready(options?: { expand?: boolean }) {
    return withTelegramWebApp(tg => {
      tg.ready();
      if (options?.expand !== false) tg.expand();
    });
  },
  close() {
    return withTelegramWebApp(tg => tg.close());
  },
  enableClosingConfirmation() {
    return withTelegramWebApp(tg => tg.enableClosingConfirmation());
  },
  disableClosingConfirmation() {
    return withTelegramWebApp(tg => tg.disableClosingConfirmation());
  },
};

const ui = {
  setHeaderColor(colorKey: Parameters<Telegram.WebApp['setHeaderColor']>[0]) {
    return withTelegramWebApp(tg => tg.setHeaderColor(colorKey));
  },
  setBottomBarColor(color: string) {
    return withTelegramWebApp(tg => tg.setBottomBarColor?.(color));
  },
};

const dataExchange = {
  send(payload: unknown) {
    return withTelegramWebApp(tg => wrapSendData(tg, payload));
  },
  switchInlineQuery(query: string, chatTypes?: Parameters<Telegram.WebApp['switchInlineQuery']>[1]) {
    return withTelegramWebApp(tg => tg.switchInlineQuery(query, chatTypes));
  },
};

const haptics = {
  impact(style?: Parameters<Telegram.HapticFeedback['impactOccurred']>[0]) {
    return withTelegramWebApp(tg => tg.HapticFeedback.impactOccurred(style));
  },
  notification(type: Parameters<Telegram.HapticFeedback['notificationOccurred']>[0]) {
    return withTelegramWebApp(tg => tg.HapticFeedback.notificationOccurred(type));
  },
  selection() {
    return withTelegramWebApp(tg => tg.HapticFeedback.selectionChanged());
  },
};

const alerts = {
  showAlert(message: string, callback?: () => void) {
    return withTelegramWebApp(tg => {
      tg.showAlert(message, callback);
    });
  },
  showConfirm(messageOrParams: string | { message: string; title?: string }, callback?: (ok: boolean) => void) {
    return withTelegramWebApp(tg => {
      tg.showConfirm(messageOrParams, callback);
    });
  },
  showPopup(params: { title?: string; message: string; buttons?: Array<{ id?: string; type?: "default" | "ok" | "close" | "cancel" | "destructive"; text: string }> }, callback?: (buttonId?: string) => void) {
    return withTelegramWebApp(tg => {
      tg.showPopup(params, callback);
    });
  },
};

export const TelegramWebAppUtils = {
  isAvailable: () => Boolean(getTelegramWebApp()),
  getInitDataUnsafe: () => assertTelegramWebApp().initDataUnsafe,
  getInitData: () => assertTelegramWebApp().initData,
  getThemeParams: () => getTelegramWebApp()?.themeParams ?? null,
  lifecycle,
  mainButton: mainButtonControls,
  secondaryButton: secondaryButtonControls,
  backButton: backButtonControls,
  events,
  fullscreen,
  orientation,
  share,
  shortcuts,
  emojiStatus,
  location,
  sensors,
  storage,
  ui,
  data: dataExchange,
  haptics,
  alerts,
};

export function onBottomButtonClick(handler: () => void): () => void {
  const tg = assertTelegramWebApp();
  tg.onEvent('mainButtonClicked', handler);
  return () => tg.offEvent('mainButtonClicked', handler);
}

export function onBack(handler: () => void): () => void {
  const tg = assertTelegramWebApp();
  tg.onEvent('backButtonClicked', handler);
  return () => tg.offEvent('backButtonClicked', handler);
}

export function sendData(payload: unknown) {
  return withTelegramWebApp(tg => wrapSendData(tg, payload));
}

export function getInitDataUnsafe() {
  const tg = assertTelegramWebApp();
  return tg.initDataUnsafe;
}

